import { z } from 'zod';

import { isLiveSession } from './enrichment-status.domain';

/**
 * Runtime contract for GET /enrichment-sessions/stats; every section the two
 * pages read: `totals` (the Sessions KPI row, whole history), `hourly_throughput`
 * (last 24 hours, whatever the period) and the period-scoped sections the Metrics
 * page draws (`period`, `daily_activity`, `period_totals`, `by_type`,
 * `recent_sessions`, `performance`).
 *
 * Tolerant on purpose: every section defaults or is optional, so a backend that
 * predates the period filter still renders; the page falls back on what it can
 * derive (see `periodTotalsOf`). Unknown keys are stripped, not rejected.
 *
 * TODO: promote this schema to @shared/schemas so backend and frontend share a
 * single source of truth.
 */

/** The window the stats are computed over. `to` is EXCLUSIVE, both are ISO instants. */
export interface StatsRange {
  from: string;
  to: string;
}

const throughputPointSchema = z.object({
  hour: z.string(),
  enriched: z.number(),
  errored: z.number(),
  skipped: z.number(),
});

/** One bucket of the period (a day, a week or a month); `date` is its first day. */
const dailyActivitySchema = z.object({
  date: z.string(),
  created: z.number(),
  completed: z.number(),
  failed: z.number(),
  profiles_enriched: z.number(),
  profiles_errored: z.number(),
});

const recentSessionSchema = z.object({
  id: z.string().optional(),
  type: z.string(),
  status: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  total_profiles: z.number().optional(),
});

// Per-connector breakdown (e.g. linkedin_standard), scoped to the period.
const byTypeSchema = z.object({
  type: z.string(),
  count: z.number(),
  enriched: z.number(),
  errored: z.number(),
});

// Aggregate counters powering the Sessions KPI row (whole history). Each field
// defaults to 0 so a partial or missing `totals` object degrades to a zeroed row.
const totalsSchema = z
  .object({
    total_sessions: z.number().default(0),
    active: z.number().default(0),
    paused: z.number().default(0),
    completed: z.number().default(0),
    failed: z.number().default(0),
    total_profiles_enriched: z.number().default(0),
    total_profiles_errored: z.number().default(0),
    total_profiles_skipped: z.number().default(0),
    total_profiles: z.number().default(0),
  })
  .prefault({});

export const GRANULARITIES = ['day', 'week', 'month'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

const periodSchema = z.object({
  from: z.string(),
  to: z.string(),
  granularity: z.enum(GRANULARITIES).catch('day'),
  time_zone: z.string().default('Europe/Paris'),
});

const periodTotalsSchema = z.object({
  sessions: z.number().default(0),
  completed: z.number().default(0),
  failed: z.number().default(0),
  /** Sessions still progressing on their own; paused ones are counted apart. */
  running: z.number().default(0),
  paused: z.number().default(0),
  profiles_enriched: z.number().default(0),
  profiles_errored: z.number().default(0),
  profiles_skipped: z.number().default(0),
});

export const BATCH_SIZE_BUCKETS = ['0-100', '100-500', '500+'] as const;
export type BatchSizeBucket = (typeof BATCH_SIZE_BUCKETS)[number];

const batchSizePerformanceSchema = z.object({
  bucket: z.enum(BATCH_SIZE_BUCKETS),
  sessions: z.number().default(0),
  median_seconds_per_profile: z.number().nullable().default(null),
  median_session_seconds: z.number().nullable().default(null),
});

/** Speed of the period's completed sessions; medians, never averages. */
const performanceSchema = z.object({
  sessions_counted: z.number().default(0),
  median_seconds_per_profile: z.number().nullable().default(null),
  median_session_seconds: z.number().nullable().default(null),
  p95_session_seconds: z.number().nullable().default(null),
  by_batch_size: z.array(batchSizePerformanceSchema).default([]),
});

export const enrichmentStatsSchema = z.object({
  totals: totalsSchema,
  by_type: z.array(byTypeSchema).default([]),
  daily_activity: z.array(dailyActivitySchema).default([]),
  hourly_throughput: z.array(throughputPointSchema).default([]),
  recent_sessions: z.array(recentSessionSchema).default([]),
  period: periodSchema.optional(),
  period_totals: periodTotalsSchema.optional(),
  performance: performanceSchema.optional(),
});

export type EnrichmentStats = z.infer<typeof enrichmentStatsSchema>;
export type ThroughputPoint = z.infer<typeof throughputPointSchema>;
export type DailyActivity = z.infer<typeof dailyActivitySchema>;
export type RecentSession = z.infer<typeof recentSessionSchema>;
export type ByTypeStat = z.infer<typeof byTypeSchema>;
export type StatsPeriod = z.infer<typeof periodSchema>;
export type PeriodTotals = z.infer<typeof periodTotalsSchema>;
export type Performance = z.infer<typeof performanceSchema>;
export type BatchSizePerformance = z.infer<typeof batchSizePerformanceSchema>;

export interface ChartData {
  throughput: ThroughputPoint[];
  activity: DailyActivity[];
  sessions: RecentSession[];
  /**
   * True when nothing happened in the selected period; lets the UI show a
   * single empty state. The last-24-hours throughput does not count: it ignores
   * the period, and a past custom range with a busy morning is still empty.
   */
  isEmpty: boolean;
}

/** True when at least one recent session is still live (drives auto-refresh). */
export function hasActiveSession(sessions: RecentSession[]): boolean {
  return sessions.some((s) => isLiveSession(s));
}

/**
 * The period's counters. A backend without the period filter sends no
 * `period_totals`: the figures are then summed from the buckets, which carry
 * everything but the running, paused and skipped counts.
 */
export function periodTotalsOf(stats: EnrichmentStats): PeriodTotals {
  if (stats.period_totals) return stats.period_totals;
  return stats.daily_activity.reduce<PeriodTotals>(
    (acc, d) => ({
      ...acc,
      sessions: acc.sessions + d.created,
      completed: acc.completed + d.completed,
      failed: acc.failed + d.failed,
      profiles_enriched: acc.profiles_enriched + d.profiles_enriched,
      profiles_errored: acc.profiles_errored + d.profiles_errored,
    }),
    {
      sessions: 0,
      completed: 0,
      failed: 0,
      running: 0,
      paused: 0,
      profiles_enriched: 0,
      profiles_errored: 0,
      profiles_skipped: 0,
    },
  );
}

/**
 * Pure seam between the API shape and the page sections: renames the `*_*` API
 * sections to chart-friendly keys and derives an `isEmpty` flag.
 */
export function selectChartData(stats: EnrichmentStats): ChartData {
  const totals = periodTotalsOf(stats);
  const quietBuckets = stats.daily_activity.every(
    (d) => d.created === 0 && d.profiles_enriched === 0 && d.profiles_errored === 0,
  );
  return {
    throughput: stats.hourly_throughput,
    activity: stats.daily_activity,
    sessions: stats.recent_sessions,
    isEmpty:
      totals.sessions === 0 &&
      totals.profiles_enriched === 0 &&
      stats.recent_sessions.length === 0 &&
      quietBuckets,
  };
}
